import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import SnippetFolderIcon from "@mui/icons-material/Folder";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ConfirmResponse, useConfirmDialog, } from "app/providers/confirm-dialog";
import { useDialog } from "app/providers/dialog";
import { CanceledError } from "axios";
import { OrderDirection } from "common/contracts/order-direction";
import PaginationParameters from "common/contracts/pagination-parameters";
import Guid from "common/values/guid/guid";
import {
  createMRTColumnHelper,
  MaterialReactTable,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  useMaterialReactTable,
} from "material-react-table";
import { MouseEvent, useEffect, useState } from "react";
import { useSession } from "users/session/session-context";
import CommentThread from "work/entities/comment-thread/comment-thread";
import CommentsButton from "work/entities/comment-thread/view/comments-button";
import FeeScheduleTemplateAPIService from "work/entities/fee-schedule-template/api/fee-schedule-template-api-service";
import FeeScheduleTemplateQuery, { FeeScheduleTemplateFilterParameters, FeeScheduleTemplateOrderField, FeeScheduleTemplateOrderParameters } from "work/entities/fee-schedule-template/api/request-contracts/query-fee-schedule-template-api-request";
import FeeScheduleTemplate from "work/entities/fee-schedule-template/fee-schedule-template";
import { ProposalField } from "work/entities/proposal/proposal";
import FeeScheduleCategoryName from "work/values/fee-schedule-category-name/fee-schedule-category-name";
import FeeScheduleCategory, {
  FeeScheduleCategoryMaskedCurrency
} from "work/values/fee-schedule-category/fee-schedule-category";
import FeeScheduleCategoryForm from "work/values/fee-schedule-category/view/fee-schedule-category-form";
import FeeScheduleExistingCategories from "work/values/fee-schedule-category/view/fee-schedule-existing-categories";
import FeeScheduleExistingTemplate from "work/values/fee-schedule-category/view/fee-schedule-existing-template";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ActionsContainer = styled(Box)(
  ({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1),
    justifyContent: "flex-end",
    marginBottom: theme.spacing(2),
  }));
const RowTitle = styled(Typography)(
  () => ({
    fontWeight: "500",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }));
const RowActionsContainer = styled("div")(
  () => ({
    whiteSpace: 'nowrap'
  }));

type FeeScheduleCategoriesProps = {
  className?: string;
  categories: Array<FeeScheduleCategory> | Array<FeeScheduleCategoryMaskedCurrency>;
  disableEditing?: boolean;
  disableCategorySelection?: boolean;
  hideFromTemplateButton?: boolean;
  hideAddButton?: boolean;
  hideExistingButton?: boolean;
  hideCommentButton?: boolean;
  hideActionsColumn?: boolean;
  commentThreads?: CommentThread[];
  commentsToolTip: string;
  activeReviewField?: ProposalField;
  onCommentsClicked?: (field: ProposalField, name?: string) => void;
  onCategoryAdded?: (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => void;
  onCategoryUpdated?: (
    originalName: FeeScheduleCategoryName | null,
    updatedCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency
  ) => void;
  onCategoryRemoved?: (name: FeeScheduleCategoryName) => void;
  onExistingTemplateSelected?: (
    template: FeeScheduleTemplate,
    replace: boolean
  ) => void;
};

export default function FeeScheduleCategories(
  props: Readonly<FeeScheduleCategoriesProps>
) {
  const {
    className,
    categories,
    disableEditing,
    disableCategorySelection,
    hideFromTemplateButton,
    hideAddButton,
    hideExistingButton,
    hideCommentButton,
    hideActionsColumn,
    commentThreads,
    commentsToolTip,
    activeReviewField,
    onCommentsClicked,
    onCategoryAdded,
    onCategoryUpdated,
    onCategoryRemoved,
    onExistingTemplateSelected,
  } = props;

  const [existingTemplates, setExistingTemplates] = useState<
    FeeScheduleTemplate[]
  >([]);
  const [existingCategories, setExistingCategories] = useState<
    FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[]
  >([]);
  const [paginatedParameters] = useState<PaginationState>(new PaginationParameters(0, 10));
  const [query, setQuery] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isLoading, setIsLoading] = useState(false);

  const session = useSession();
  const confirm = useConfirmDialog();
  const { openDialog, popDialog } = useDialog();

  useEffect(
    () => {
      let abortController = new AbortController();
      void getExistingTemplatesAndCategories(abortController);
      return () => {
        abortController.abort();
        abortController = new AbortController();
      };
    },
    [
      columnFilters,
      query,
      paginatedParameters?.pageIndex,
      paginatedParameters?.pageSize,
      sorting
    ]
  );

  async function getExistingTemplatesAndCategories(abortController: AbortController) {
    if (!session.user?.isCompanyManager) return;

    setIsLoading(true);
    try {
      const entityId = session.currentEntity.entityId;

      const filterParams = new FeeScheduleTemplateFilterParameters(
        entityId
      );
      const orderParameters = new FeeScheduleTemplateOrderParameters(
        FeeScheduleTemplateOrderField.Name,
        OrderDirection.Descending
      );

      const templateQuery: FeeScheduleTemplateQuery = new FeeScheduleTemplateQuery(
        filterParams,
        orderParameters,
        paginatedParameters as PaginationParameters
      );
      const feeScheduleService = new FeeScheduleTemplateAPIService(session);
      const templates =
        await feeScheduleService.getFeeScheduleTemplates(
          templateQuery,
          abortController
        );
      setExistingTemplates(templates.data);

      const existingCategories: FeeScheduleCategory[] = [];
      const existingCategoriesMasked: FeeScheduleCategoryMaskedCurrency[] = [];
      templates.data.forEach((template) => {
        template.categories.forEach(category => {
          if (category instanceof FeeScheduleCategoryMaskedCurrency)
            existingCategoriesMasked.push(category);
          else
            existingCategories.push(category);
        });
      });

      setExistingCategories(existingCategories.length > 0 ? existingCategories : existingCategoriesMasked);
      setRowCount(existingCategories.length);
    } catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleRemoveButtonClicked(
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>,
    category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | undefined | null
  ) {
    event.stopPropagation();

    if (!category) return;

    const response = await confirm({
      title: "Delete Fee Schedule Category?",
      message: `Delete the ${category.name} fee schedule category?`,
      okButtonText: "Delete",
      cancelButtonText: "Cancel",
    });

    if (response === ConfirmResponse.Cancel) return;

    onCategoryRemoved?.(category.name);
  }

  function beginAddCategory() {
    openDialog({
      title: "Create Fee Schedule Category",
      component: (
        <FeeScheduleCategoryForm
          onCategoryAdded={onCategoryAdded}
          popDialog={popDialog}
        />
      ),
    });
  }

  function beginEditCategory(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | undefined | null) {
    if (!category || disableEditing) return;

    openDialog({
      title: "Edit Fee Schedule Category",
      component: (
        <FeeScheduleCategoryForm
          category={category}
          onCategoryUpdated={onCategoryUpdated}
          popDialog={popDialog}
        />
      ),
    });
  }

  function handleExistingCategoryClicked() {
    openDialog({
      title: "Add Existing Fee Schedule Category",
      component: (
        <FeeScheduleExistingCategories
          categories={existingCategories}
          disabledCategories={categories}
          onCategoriesSelected={handleExistingCategoriesSelected}
        />
      ),
    });
  }

  function handleExistingTemplateClicked() {
    openDialog({
      title: "Select Existing Fee Schedule Template",
      component: (
        <FeeScheduleExistingTemplate
          onTemplateSelected={handleExistingTemplateSelected}
        />
      ),
    });
  }

  async function handleExistingTemplateSelected(template: FeeScheduleTemplate) {
    let replace: boolean = false;

    if (categories.length > 0) {
      const response = await confirm({
        title: "Replace Or Add Categories?",
        message: `Do you want to replace the current categories with the selected template's categories or add the selected template's categories to the current categories?`,
        okButtonText: "Replace",
        alternativeOkText: "Add",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;

      replace = response === ConfirmResponse.Ok;
    }

    popDialog();
    onExistingTemplateSelected?.(
      template,
      replace
    );
  }


  function handleExistingCategoriesSelected(categories: FeeScheduleCategory[]) {
    popDialog();
    categories.forEach((category) => {
      onCategoryAdded?.(category);
    });
  }

  const columnHelper = createMRTColumnHelper<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>();
  const columns = [
    columnHelper.accessor(
      (category) => <RowTitle>{category.name.value}</RowTitle>,
      {
        header: "Name",
        id: "name",
      }
    ),
    columnHelper.accessor(
      (category) => category.description?.value,
      {
        header: "Description",
        id: "description",
      }
    ),
    columnHelper.accessor(
      (category) => {
        let fixedValue;
        if (category instanceof FeeScheduleCategoryMaskedCurrency) {
          fixedValue = "Masked";
        } else if (!category?.fee?.unit) {
          fixedValue = "Yes";
        } else {
          fixedValue = "No";
        }
        return fixedValue;
      },
      {
        header: "Fixed?",
        id: "fixedFee",
        size: 50,
      }
    ),
    columnHelper.accessor(
      (category) => {
        let feeValue;
        if (category instanceof FeeScheduleCategoryMaskedCurrency) {
          feeValue = "Masked";
        } else if (!category.fee) {
          feeValue = (
            <Typography color="warning">{`Deferred to ${session.context?.viewingAsVendor ? "client" : "vendor"}`}</Typography>
          );
        } else {
          feeValue = new Intl.NumberFormat(
            navigator.language,
            {
              style: "currency",
              currency: category.fee?.rate?.currency.toString() ?? "USD",
            }
          ).format(category.fee?.rate?.amount ?? 0);
        }
        return feeValue;
      },
      {
        header: "Fee",
        id: "fee",
        size: 50
      }
    ),
    columnHelper.accessor(
      (category) =>
        category instanceof FeeScheduleCategoryMaskedCurrency ? "Masked" :
          category.fee?.unit ?? <Icon>remove</Icon>,
      {
        header: "Per",
        id: "unit",
        size: 50
      }
    ),
    columnHelper.accessor(
      (category) =>
        category.billingCode?.value && category.billingCode.value.length > 0 ? (
          category.billingCode.value
        ) : (
          <Icon>remove</Icon>
        ),
      {
        header: "Code",
        id: "billingCode",
        size: 50
      }
    ),
  ];
  const table = useMaterialReactTable({
    columns,
    data: categories,
    enableRowSelection: false,
    enableTableHead: true,
    getRowId: (row) => row.id?.toString() ?? Guid.generate().toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: { right: ['mrt-row-actions'] }
    },
    enableColumnPinning: true,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    enableRowActions: !hideActionsColumn,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setQuery,
    onSortingChange: setSorting,
    rowCount,
    state: {
      columnFilters,
      globalFilter: query,
      isLoading,
      sorting,
    },
    muiTableProps: ({ table }) => ({
      sx: {
        borderSpacing: table
          .getRowModel()
          .rows.find((row) => row.id === activeReviewField?.id?.toString())
          ? "3px"
          : 0
      },
    }),
    muiTableBodyRowProps: ({ row }) => !disableCategorySelection ? ({
      onClick: () => beginEditCategory(row.original),
      sx: {
        animation: row?.original?.id?.isEqualTo(activeReviewField?.id)
          ? `${pulseBorder} 1.5s infinite`
          : "none",
        cursor: "pointer"
      },
    }) : {},
    renderTopToolbarCustomActions: () => {
      return (
        <ActionsContainer>
          {!disableEditing && !hideAddButton && (
            <Button
              startIcon={<AddIcon />}
              onClick={beginAddCategory}
              variant="outlined"
            >
              Add New Category
            </Button>
          )}
          {!disableEditing && !hideExistingButton && (
            <Button
              startIcon={<SnippetFolderIcon />}
              disabled={existingCategories.length === 0}
              onClick={handleExistingCategoryClicked}
              variant="outlined"
            >
              Add Existing Category
            </Button>
          )}
          {!disableEditing && !hideFromTemplateButton && (
            <Button
              startIcon={<FileCopyIcon />}
              disabled={existingTemplates.length === 0}
              onClick={handleExistingTemplateClicked}
              variant="outlined"
            >
              From Existing Template
            </Button>
          )}
        </ActionsContainer>
      );
    },
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <ToggleGlobalFilterButton table={table} />
        <ToggleFiltersButton table={table} />
        <ShowHideColumnsButton table={table} />
        <ToggleDensePaddingButton table={table} />
        <ToggleFullScreenButton table={table} />
        {!hideCommentButton && (
          <CommentsButton
            commentThreads={commentThreads}
            field={ProposalField.FeeSchedule}
            isActive={activeReviewField?.isEqualTo(ProposalField.FeeSchedule)}
            toolTip={commentsToolTip}
            onCommentsClicked={onCommentsClicked ? () => {
              onCommentsClicked?.(ProposalField.FeeSchedule);
            } : undefined}
          />
        )}
      </Box>
    ),
    renderRowActions: ({ row }) => {
      return (
        <RowActionsContainer>
          {!disableEditing && (
            <Tooltip title="Remove Category">
              <span>
                <IconButton
                  onClick={(event) => handleRemoveButtonClicked(
                    event,
                    row.original
                  )}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {!hideCommentButton && (
            <CommentsButton
              field={ProposalField.FeeScheduleCategory(row.original.id)}
              isActive={activeReviewField?.isEqualTo(ProposalField.FeeScheduleCategory(row.original.id))}
              commentThreads={commentThreads}
              toolTip={commentsToolTip}
              onCommentsClicked={onCommentsClicked ? () => {
                onCommentsClicked(
                  ProposalField.FeeScheduleCategory(row.original.id),
                  row.original.name.value
                );
              } : undefined}
            />
          )}
        </RowActionsContainer>
      );
    },
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        height: "100%",
      },
    },
  });

  return (
    <div className={className}>
      <MaterialReactTable table={table} />
    </div>
  );
}
